import { t as uiText, currentUiLanguage } from '../i18n';
import React from 'react';
import { useParams } from 'react-router-dom';
import './UpgradeModal.css';
import { useWorkspaceContext } from '../context/WorkspaceContext';
import { isNcgSeries, SERIES_NCG, SERIES_NCN } from '../features/plans/series';
import { adminSetWorkspacePlan } from '../features/serviceAdmin/callables';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import PlanChangeFlowModal from './PlanChangeFlowModal';
import { NcgLaunchOfferNotice } from './billing/NcgLaunchOfferNotice';

type PlanId = 'free' | 'starter' | 'standard' | 'team';

/** 販売開始までプラン一覧に出さない。金額は ¥14,800。 */
const SHOW_NCN_TEAM_PLAN = false;

type PlanSchedule = {
  currentPlanId: string;
  currentPeriodEnd: Date | null;
  planChangePendingPlanId: string;
  cancelAtPeriodEnd: boolean;
  stripeSubscriptionId: string;
};

function readPlanSchedule(data: Record<string, unknown> | undefined): PlanSchedule {
  const planId = typeof data?.planId === 'string' ? data.planId.trim() : '';
  const pending =
    typeof data?.planChangePendingPlanId === 'string' ? data.planChangePendingPlanId.trim() : '';
  const subId =
    typeof data?.stripeSubscriptionId === 'string' ? data.stripeSubscriptionId.trim() : '';
  let currentPeriodEnd: Date | null = null;
  const rawEnd = data?.currentPeriodEnd;
  if (rawEnd instanceof Date && !Number.isNaN(rawEnd.getTime())) {
    currentPeriodEnd = rawEnd;
  } else if (rawEnd && typeof rawEnd === 'object' && typeof (rawEnd as { toDate?: unknown }).toDate === 'function') {
    const d = (rawEnd as { toDate: () => Date }).toDate();
    if (d instanceof Date && !Number.isNaN(d.getTime())) currentPeriodEnd = d;
  }
  return {
    currentPlanId: planId || 'free',
    currentPeriodEnd,
    planChangePendingPlanId: pending,
    cancelAtPeriodEnd: data?.cancelAtPeriodEnd === true,
    stripeSubscriptionId: subId,
  };
}

function shouldShowPlanSchedule(s: PlanSchedule): boolean {
  return (
    s.cancelAtPeriodEnd
    || (!!s.planChangePendingPlanId
      && !!s.stripeSubscriptionId
      && s.planChangePendingPlanId !== s.currentPlanId)
  );
}

function nextPlanFromSchedule(s: PlanSchedule): string {
  if (s.cancelAtPeriodEnd || s.planChangePendingPlanId === 'free') return 'free';
  return s.planChangePendingPlanId;
}

function formatPeriodEndDate(date: Date): string {
  const locale = currentUiLanguage() === 'ja' ? 'ja-JP' : 'en-US';
  return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
}

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * 指定された場合、このモーダルは「プラン選択モード」になります。
   * 未指定の場合は従来通り「プラン一覧表示モード」です。
   */
  onSelectPlan?: (planId: PlanId) => void;
  currentPlanId?: PlanId | null;
  /** 上限到達などからの導線で、おすすめのプランを強調する */
  highlightPlanId?: PlanId | null;
  /** 開いた理由（上限に達した旨など）。ヘッダー下に一行表示する */
  reason?: string;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  onSelectPlan,
  currentPlanId = null,
  highlightPlanId = null,
  reason,
}) => {
  const { workspaceId: routeWorkspaceId } = useParams();
  const { refresh } = useWorkspaceContext();
  const [isRegisteringExclusive, setIsRegisteringExclusive] = React.useState(false);
  // NCG Starter ????????????????????Service Admin ???????????????
  // null = ????????? false????????????Checkout ?????????????
  const [ncgCampaignActive, setNcgCampaignActive] = React.useState<boolean | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    httpsCallable<unknown, { active: boolean }>(functions, 'getNcgStarterCampaignStatusFn')({})
      .then((res) => {
        if (!cancelled) setNcgCampaignActive(res.data?.active === true);
      })
      .catch(() => {
        if (!cancelled) setNcgCampaignActive(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const [currentPlanIdForDisplay, setCurrentPlanIdForDisplay] = React.useState<PlanId | null>(currentPlanId);
  const [workspacePlanId, setWorkspacePlanId] = React.useState<string>('free');
  const [workspaceSeriesId, setWorkspaceSeriesId] = React.useState<string>('');
  const [isChildWorkspace, setIsChildWorkspace] = React.useState(false);
  const [inheritedPlanLabel, setInheritedPlanLabel] = React.useState<string | null>(null);
  const [planChangeTarget, setPlanChangeTarget] = React.useState<PlanId | null>(null);
  const [planSchedule, setPlanSchedule] = React.useState<PlanSchedule | null>(null);

  const isSelectMode = typeof onSelectPlan === 'function';
  const isNcg = isNcgSeries(workspaceSeriesId);
  // 解約後は契約 ID が残る。初回限定価格は戻さない
  const resubscribeAtRegularPrice = workspacePlanId === 'free' && !!planSchedule?.stripeSubscriptionId;
  const showLaunchPrice = isNcg && ncgCampaignActive === true && !resubscribeAtRegularPrice;

  // ヘッダー表示用: FileStatsDisplay/MemberListModal と同じ方法でplanIdを取得
  React.useEffect(() => {
    let cancelled = false;
    const fetchWorkspacePlan = async () => {
      if (!isOpen || !routeWorkspaceId) {
        if (!cancelled && !routeWorkspaceId) {
          setWorkspacePlanId('free');
          setWorkspaceSeriesId('');
          setIsChildWorkspace(false);
          setInheritedPlanLabel(null);
          setPlanSchedule(null);
        }
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'workspaces', routeWorkspaceId));
        if (!snap.exists()) {
          if (!cancelled) {
            setWorkspacePlanId('free');
            setWorkspaceSeriesId('');
            setIsChildWorkspace(false);
            setInheritedPlanLabel(null);
            setPlanSchedule(null);
          }
          return;
        }
        const data = snap.data() as Record<string, unknown>;
        const raw = typeof data?.planId === 'string' ? data.planId.trim() : '';
        const seriesRaw = typeof data?.seriesId === 'string' ? data.seriesId.trim() : '';
        const parentId = typeof data?.parentWorkspaceId === 'string' ? data.parentWorkspaceId.trim() : '';
        let inherited: string | null = null;
        let schedule = readPlanSchedule(data);
        if (parentId) {
          inherited = raw && raw !== 'child' ? raw : null;
          try {
            const parentSnap = await getDoc(doc(db, 'workspaces', parentId));
            const parentData = parentSnap.data() as Record<string, unknown> | undefined;
            const parentPlan =
              typeof parentData?.planId === 'string' ? parentData.planId.trim() : '';
            if (!inherited) {
              inherited = parentPlan && parentPlan !== 'child' ? parentPlan : 'free';
            }
            if (parentSnap.exists()) {
              schedule = readPlanSchedule(parentData);
            }
          } catch {
            if (!inherited) inherited = 'free';
          }
        }
        if (shouldShowPlanSchedule(schedule) && !schedule.currentPeriodEnd && schedule.stripeSubscriptionId) {
          try {
            const mode = import.meta.env.MODE === 'production' ? 'live' : 'test';
            const fn = httpsCallable(functions, 'syncWorkspaceBillingScheduleFn');
            const res = await fn({
              workspaceId: routeWorkspaceId,
              mode,
            });
            const dataRes = res.data as { currentPeriodEndMs?: number | null };
            if (typeof dataRes?.currentPeriodEndMs === 'number' && dataRes.currentPeriodEndMs > 0) {
              schedule = {
                ...schedule,
                currentPeriodEnd: new Date(dataRes.currentPeriodEndMs),
              };
            }
          } catch (e) {
            console.warn('[UpgradeModal] sync billing schedule failed', e);
          }
        }
        if (!cancelled) {
          setWorkspacePlanId(raw || 'free');
          setWorkspaceSeriesId(seriesRaw);
          setIsChildWorkspace(!!parentId);
          setInheritedPlanLabel(inherited);
          setPlanSchedule(schedule);
        }
      } catch (e) {
        console.error('[UpgradeModal] Failed to load workspace planId:', e);
        if (!cancelled) {
          setWorkspacePlanId('free');
          setWorkspaceSeriesId('');
          setIsChildWorkspace(false);
          setInheritedPlanLabel(null);
          setPlanSchedule(null);
        }
      }
    };
    void fetchWorkspacePlan();
    return () => { cancelled = true; };
  }, [routeWorkspaceId, isOpen, planChangeTarget]);

  // 表示モードでは、現在のワークスペースのplanIdを反映する（renderPlanButton用）
  React.useEffect(() => {
    if (isSelectMode) {
      setCurrentPlanIdForDisplay(currentPlanId);
      return;
    }

    let cancelled = false;
    const run = async () => {
      if (!routeWorkspaceId) {
        if (!cancelled) setCurrentPlanIdForDisplay(null);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'workspaces', routeWorkspaceId));
        if (!snap.exists()) {
          if (!cancelled) setCurrentPlanIdForDisplay(null);
          return;
        }
        const data = snap.data() as any;
        const raw = typeof data?.planId === 'string' ? data.planId.trim().toLowerCase() : '';

        // exclusive は秘密プランなので「現在のプラン」ボタン表示をしない
        if (raw === 'exclusive') {
          if (!cancelled) setCurrentPlanIdForDisplay(null);
          return;
        }

        // planId が空/null/不正の場合は free 扱い
        const normalized: PlanId =
          raw === 'free' || raw === 'starter' || raw === 'standard' || raw === 'team'
            ? (raw as PlanId)
            : 'free';
        if (!cancelled) setCurrentPlanIdForDisplay(normalized);
      } catch (e) {
        console.error('[UpgradeModal] failed to load current planId', e);
        if (!cancelled) setCurrentPlanIdForDisplay(null);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [isSelectMode, currentPlanId, routeWorkspaceId]);

  const renderPlanButton = (planId: PlanId) => {
    if (planId === 'standard') {
      return (
        <button type="button" className="plan-button current" disabled>
          {uiText("Coming soon")}
        </button>
      );
    }

    if (isSelectMode) {
      const isCurrent = currentPlanId === planId;
      return (
        <button
          type="button"
          className={isCurrent ? 'plan-button current' : 'plan-button upgrade'}
          onClick={() => onSelectPlan?.(planId)}
        >
          {isCurrent ? uiText("Selecting") : uiText("Select this plan")}
        </button>
      );
    }

    const isCurrent = currentPlanIdForDisplay === planId;
    return (
      <button
        type="button"
        className={isCurrent ? 'plan-button current' : 'plan-button upgrade'}
        disabled={!routeWorkspaceId || isCurrent}
        onClick={() => {
          if (!routeWorkspaceId) return;
          if (isCurrent) return;
          if (isNcg && planId === 'team') return;
          setPlanChangeTarget(planId);
        }}
        title={!routeWorkspaceId ? uiText("No workspace selected") : (isCurrent ? uiText("This is your current plan") : uiText("Change plan"))}
      >
        {isCurrent ? uiText("Current plan") : uiText("Change plan")}
      </button>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="upgrade-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="upgrade-modal-header">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <h2>{isNcg ? uiText("notreu Concierge Guide plans") : uiText("notreu Collaborative Note plans")}</h2>
            <div style={{ fontSize: '12px', color: '#666', fontWeight: 600 }}>
              {uiText("Current plan:")}<span style={{ color: '#333' }}>
                {isChildWorkspace
                  ? uiText("{{v0}} (inherits parent plan)", { v0: inheritedPlanLabel || (workspacePlanId === 'child' ? 'free' : workspacePlanId) || 'free' })
                  : (workspacePlanId || 'free')}
              </span>
              {' ／ '}{uiText("Series:")}<span style={{ color: '#333' }}>{isNcg ? 'Concierge Guide' : 'Collaborative Note'}</span>
            </div>
            {planSchedule && shouldShowPlanSchedule(planSchedule) && (
              <div style={{ fontSize: '12px', color: '#666', fontWeight: 600 }}>
                {planSchedule.currentPeriodEnd
                  ? uiText("{{current}} applies until {{date}}. After that: {{next}}", {
                    current: planSchedule.currentPlanId,
                    date: formatPeriodEndDate(planSchedule.currentPeriodEnd),
                    next: nextPlanFromSchedule(planSchedule),
                  })
                  : uiText("{{current}} applies until the end of the period. After that: {{next}}", {
                    current: planSchedule.currentPlanId,
                    next: nextPlanFromSchedule(planSchedule),
                  })}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {import.meta.env.MODE === 'development' && !isSelectMode && (
              <button
                type="button"
                className="upgrade-modal-dev-exclusive"
                disabled={!routeWorkspaceId || isRegisteringExclusive}
                onClick={async () => {
                  if (!routeWorkspaceId) return;
                  try {
                    setIsRegisteringExclusive(true);
                    await adminSetWorkspacePlan({
                      workspaceId: routeWorkspaceId,
                      planId: 'exclusive',
                      seriesId: isNcg ? SERIES_NCG : SERIES_NCN,
                    });
                    refresh();
                    alert(uiText("Registered for exclusive"));
                  } catch (e) {
                    console.error('[UpgradeModal] failed to set exclusive plan', e);
                    alert(uiText("Failed to register for exclusive"));
                  } finally {
                    setIsRegisteringExclusive(false);
                  }
                }}
                title={!routeWorkspaceId ? uiText("No workspace selected") : uiText("For development: change the plan to exclusive")}
              >
                {isRegisteringExclusive ? uiText("Registering...") : uiText("Register for exclusive")}
              </button>
            )}
            <button className="upgrade-modal-close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>
        
        <div className="upgrade-modal-body">
          {reason && (
            <div className="upgrade-modal-reason" role="status">
              {reason}
            </div>
          )}
          <div className="upgrade-plans">
            <div className={`plan-card${highlightPlanId === 'free' ? ' plan-card--recommended' : ''}`}>
              <h3>free</h3>
              <div className="plan-price plan-price--free">
                <div className="plan-price-free-main">¥0<span>{uiText("/month")}</span></div>
                <div className="plan-price-free-note">{uiText("No charge")}</div>
              </div>
                <ul className="plan-features">
                  <li>{uiText("Page count: unlimited")}</li>
                  <li>{uiText("Members: up to 3")}</li>
                  <li>{uiText("Add members: unavailable")}</li>
                  <li>{uiText("File attachments: 1GB")}</li>
                  <li>{uiText("Extra storage: Unavailable")}</li>
                  <li>{uiText("AI credits (internal AI): 1,200/month")}</li>
                  {isNcg && <li>{uiText("AI credits (public AI): 80/month")}</li>}
                  {isNcg && <li>{uiText("Crawl: up to 2,000 URLs/day, 20,000 URLs/month")}</li>}
                  <li>{uiText("Basic features")}</li>
                </ul>
              {renderPlanButton('free')}
            </div>
            
            <div className={`plan-card${showLaunchPrice ? ' plan-card--promo' : ''}${highlightPlanId === 'starter' ? ' plan-card--recommended' : ''}`}>
              {showLaunchPrice && (
                <span className="plan-campaign-badge">{uiText("Launch offer")}</span>
              )}
              <h3>starter</h3>
              {showLaunchPrice ? (
                <div className="plan-price-promo-block">
                  <div className="plan-price-original">
                    <s>¥14,800</s>
                    <span className="plan-tax">{uiText("(excl. tax)")}</span>
                  </div>
                  <div className="plan-price-promo">
                    ¥9,800<span>{uiText("/month")}</span>
                    <span className="plan-tax">{uiText("(excl. tax)")}</span>
                  </div>
                  <div className="plan-price-period">{uiText("For the first 12 months")}</div>
                  <div className="plan-price-after">
                    {uiText("From month 13: ¥14,800/month (excl. tax)")}
                  </div>
                  <NcgLaunchOfferNotice />
                </div>
              ) : (
                <div>
                  <div className="plan-price">{isNcg ? '¥14,800' : '¥1,000'}<span>{uiText("/month")}</span></div>
                  {isNcg && resubscribeAtRegularPrice && <NcgLaunchOfferNotice part="resubscribe" />}
                </div>
              )}
                <ul className="plan-features">
                  <li>{uiText("Page count: unlimited")}</li>
                  <li>{uiText("Members: up to 10")}</li>
                  <li>{uiText("Add members: unavailable")}</li>
                  <li>{uiText("File attachments: 10GB")}</li>
                  <li>{uiText("Extra storage: ¥300/10GB per month")}</li>
                  <li>{uiText("AI credits (internal AI): 2,400/month")}</li>
                  {isNcg && <li>{uiText("AI credits (public AI): 2,400/month")}</li>}
                  {isNcg && <li>{uiText("Crawl: up to 2,000 URLs/day, 20,000 URLs/month")}</li>}
                  <li>{uiText("Basic features")}</li>
                </ul>
              {renderPlanButton('starter')}
            </div>

            {!isNcg && (
            <div className={`plan-card${highlightPlanId === 'standard' ? ' plan-card--recommended' : ''}`}>
              <h3>standard</h3>
              <div className="plan-price">¥5,000<span>{uiText("/month")}</span></div>
                <ul className="plan-features">
                  <li>{uiText("Page count: unlimited")}</li>
                  <li>{uiText("Members: up to 30")}</li>
                  <li>{uiText("Add members: ¥300/month per person")}</li>
                  <li>{uiText("File attachments: 100GB")}</li>
                  <li>{uiText("Extra storage: ¥300/10GB per month")}</li>
                  <li>{uiText("AI credits (internal AI): 8,000/month")}</li>
                  <li>{uiText("Advanced features")}</li>
                </ul>
              {renderPlanButton('standard')}
            </div>
            )}

            {SHOW_NCN_TEAM_PLAN && !isNcg && (
            <div className="plan-card">
              <h3>team</h3>
              <div className="plan-price">¥14,800<span>{uiText("/month")}</span></div>
                <ul className="plan-features">
                  <li>{uiText("Page count: unlimited")}</li>
                  <li>{uiText("Members: up to 100")}</li>
                  <li>{uiText("Add members: ¥500/month per person")}</li>
                  <li>{uiText("File attachments: 500GB")}</li>
                  <li>{uiText("Extra storage: ¥300/10GB per month")}</li>
                  <li>{uiText("AI credits (internal AI): 8,000/month")}</li>
                  <li>{uiText("All features")}</li>
                </ul>
              {renderPlanButton('team')}
            </div>
            )}
          </div>
        </div>
      </div>

      {!!routeWorkspaceId && !!planChangeTarget && !isSelectMode && (
        <PlanChangeFlowModal
          isOpen={true}
          workspaceId={routeWorkspaceId}
          targetPlanId={planChangeTarget}
          expectedCampaign={
            resubscribeAtRegularPrice
              ? false
              : isNcg && ncgCampaignActive !== null
                ? ncgCampaignActive
                : undefined
          }
          onClose={() => setPlanChangeTarget(null)}
        />
      )}
    </div>
  );
};

export default UpgradeModal;
